import React, { useState, ChangeEvent } from 'react';
import styles from './picker.module.scss';
import { Box } from '@mui/material';
import Tabs from '../Tabs/Tabs';
import Card from '../Card/Card';
import TextInput from '../Input/TextInput';
import { Stack } from '../Stack/Stack';
import Typography from '../Typography/Typography';
import Spacer from '../Spacer/Spacer';
import Button from '../Button/Button';
import clsx from 'clsx';
import Calendar from '../Calendar/Calendar';
import { useRelativePickerState } from './useRelativePickerState';
import { useTranslation } from 'utils/translation';
import { useShallowEqualEffect } from '../../../utils/use-shallow-effect';

interface RelativeOption {
  label: string;
  value: string;
}

interface RelativeDatePickerProps {
  onChange?: (value: string | { from?: Date; to?: Date }) => void;
  defaultValue?: string | null | undefined;
}

const RELATIVE_OPTIONS: Record<string, RelativeOption[]> = {
  last: [
    { label: 'week', value: 'last_week' },
    { label: 'month', value: 'last_month' },
    { label: 'quarter', value: 'last_quarter' },
  ],
  current: [
    { label: 'week', value: 'current_week' },
    { label: 'month', value: 'current_month' },
    { label: 'quarter', value: 'current_quarter' },
  ],
  next: [
    { label: 'week', value: 'next_week' },
    { label: 'month', value: 'next_month' },
    { label: 'quarter', value: 'next_quarter' },
  ],
};

const TABS = ['last', 'current', 'next'];

const areMonthsEqual = (a: Date, b: Date) => {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
};

const RelativeDatePicker: React.FC<RelativeDatePickerProps> = ({
  defaultValue = null,
  onChange,
}) => {
  const { t } = useTranslation();
  const {
    isExpression,
    value,
    period,
    setRange,
    setExpression,
    getSelectedRange,
    setCustomDays,
  } = useRelativePickerState(defaultValue);
  const [month, setMonth] = useState(new Date());
  const selectedRange = getSelectedRange();
  const from = selectedRange?.from?.toISOString() || null;

  useShallowEqualEffect(() => {
    if (selectedRange?.from && !areMonthsEqual(selectedRange.from, month)) {
      setMonth(selectedRange.from);
    }
  }, [from]);

  const initialTab = Object.keys(RELATIVE_OPTIONS).findIndex((key) => {
    return isExpression && key === String(value)?.split('_')[0];
  });
  const [activeTab, setActiveTab] = useState<number>(
    initialTab >= 0 ? initialTab : 0,
  );
  const currentTabKey = TABS[activeTab];

  const handleCustomPeriodChange = (
    mode: 'last' | 'next',
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const asNumber = Number(e.target.value?.replace(/\D/g, '').slice(0, 3));
    setCustomDays(mode, asNumber);
  };

  const onApply = () => {
    onChange?.(value);
  };

  return (
    <Card shadow className={styles.root}>
      <Card.Content className={styles.content}>
        <div className={styles.container}>
          <div>
            <Calendar
              month={month}
              onMonthChange={setMonth}
              mode="range"
              selected={selectedRange}
              onSelect={(selected) => {
                if (selected) {
                  setRange(selected.from, selected?.to);
                }
              }}
            />
          </div>
          <div className={styles.divider} />
          <Box className={styles.tabsContainer} flexGrow={1}>
            <Spacer size={3} />
            <Tabs
              size="small"
              text
              value={activeTab}
              onChange={(event, index) => {
                setActiveTab(Number(index));
                setExpression('');
              }}
            >
              <Tabs.List>
                {TABS.map((tab) => (
                  <Tabs.Tab key={tab}>
                    {t(`relative_date_picker.${tab}`)}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {TABS.map((tab, index) => (
                <Tabs.Panel key={tab} value={index}>
                  <ul className={styles.list}>
                    {RELATIVE_OPTIONS[currentTabKey].map((option) => (
                      <li
                        className={clsx(
                          styles.item,
                          isExpression &&
                            option.value === value &&
                            styles.active,
                        )}
                        key={option.value}
                        onClick={() => setExpression(option.value)}
                      >
                        {t(`relative_date_picker.${option.label}`)}
                      </li>
                    ))}
                    <li
                      className={clsx(
                        styles.item,
                        tab === 'current' && styles.disabled,
                      )}
                    >
                      <Stack direction="row" gap={1} align="center">
                        <TextInput
                          disabled={tab === 'current'}
                          style={{ width: '60px' }}
                          size="small"
                          value={period}
                          onChange={(event: any) => {
                            if (['last', 'next'].includes(tab)) {
                              handleCustomPeriodChange(
                                tab as 'last' | 'next',
                                event,
                              );
                            }
                          }}
                          onKeyDown={(event: any) => {
                            if (event.key === 'Enter') {
                              onApply();
                            }
                          }}
                        />
                        <Typography
                          className={clsx(
                            styles.label,
                            period && styles.active,
                          )}
                        >
                          {t('relative_date_picker.days')}
                        </Typography>
                      </Stack>
                    </li>
                  </ul>
                </Tabs.Panel>
              ))}
            </Tabs>
            <Spacer size={2} grow />
          </Box>
        </div>
        <Spacer size={2} />
        <Button expand size="small" onClick={onApply}>
          {t('relative_date_picker.apply')}
        </Button>
      </Card.Content>
    </Card>
  );
};

export default RelativeDatePicker;
